/**
 * QuickDmModal — Global Slack DM composer.
 *
 * Trigger via window event:
 *   window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
 *     detail: {
 *       user_id: 'U123',         // optional - if omitted, modal shows recipient picker
 *       team_id: 'T...',
 *       name: 'Stuart Meyler',
 *       prefillText: 'Just kicked this off — take a look:\nhttps://...'
 *     }
 *   }));
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { X, Send, MessageSquare, Loader2, Search } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { authFetch } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_LEN = 3000;

const Avatar = ({ name, image, size = 28 }) => {
  if (image) {
    return (
      <img src={image} alt="" className="rounded-full object-cover flex-shrink-0" style={{ width: size, height: size }} />
    );
  }
  const initials = (name || '?').split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  return (
    <div
      className="rounded-full bg-chart-purple/10 text-chart-purple font-semibold flex items-center justify-center flex-shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initials}
    </div>
  );
};

const QuickDmModal = () => {
  const [target, setTarget] = useState(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);

  // Recipient picker state (used when modal is opened without a target)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [query, setQuery] = useState('');

  const textareaRef = useRef(null);

  const open = useCallback((evt) => {
    const detail = evt.detail || {};
    setText(detail.prefillText || '');
    if (detail.user_id) {
      setTarget({
        user_id: detail.user_id,
        team_id: detail.team_id,
        name: detail.name,
        image: detail.image,
      });
      setPickerOpen(false);
      setTimeout(() => textareaRef.current?.focus(), 50);
    } else {
      // Recipient picker mode
      setTarget(null);
      setPickerOpen(true);
      setQuery('');
    }
  }, []);

  // Lazy-load directory when picker opens
  useEffect(() => {
    if (!pickerOpen || members.length) return;
    let active = true;
    setMembersLoading(true);
    (async () => {
      try {
        const res = await authFetch(`${API}/api/telzio/directory`);
        if (active && res.ok) {
          const data = await res.json();
          setMembers((data.members || []).filter((m) => m.slack?.user_id));
        }
      } catch { /* */ }
      finally { if (active) setMembersLoading(false); }
    })();
    return () => { active = false; };
  }, [pickerOpen, members.length]);

  const close = useCallback(() => {
    if (sending) return;
    setTarget(null);
    setText('');
    setPickerOpen(false);
    setQuery('');
  }, [sending]);

  useEffect(() => {
    window.addEventListener('ventas:slack:dm', open);
    return () => window.removeEventListener('ventas:slack:dm', open);
  }, [open]);

  const send = useCallback(async () => {
    if (!target || !text.trim() || sending) return;
    setSending(true);
    try {
      const res = await authFetch(`${API}/api/slack/dm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to_user_id: target.user_id, text: text.trim() }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `Send failed (${res.status})`);
      }
      toast.success(`DM sent to ${target.name || 'Slack'}`);
      setTarget(null);
      setText('');
    } catch (e) {
      toast.error('Slack DM failed', { description: e.message });
    } finally {
      setSending(false);
    }
  }, [target, text, sending]);

  // Esc closes; Cmd/Ctrl+Enter sends
  useEffect(() => {
    if (!target && !pickerOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') close();
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        send();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [target, pickerOpen, close, send]);

  const filteredMembers = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return members.slice(0, 50);
    return members.filter((m) =>
      m.full_name?.toLowerCase().includes(q) ||
      m.email?.toLowerCase().includes(q) ||
      m.title?.toLowerCase().includes(q)
    ).slice(0, 50);
  }, [members, query]);

  if (!target && !pickerOpen) return null;

  const remaining = MAX_LEN - text.length;
  const overLimit = remaining < 0;

  return (
    <div
      className="fixed inset-0 z-[110] bg-black/40 flex items-center justify-center p-4"
      onClick={close}
      data-testid="quick-dm-modal"
    >
      <div
        className="bg-surface-card rounded-xl shadow-2xl w-full max-w-md overflow-hidden flex flex-col"
        style={{ maxHeight: '85vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-border-subtle">
          <div className="w-9 h-9 rounded-md bg-chart-purple/10 flex items-center justify-center text-chart-purple">
            <MessageSquare size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs text-text-tertiary">Slack DM</p>
            {target ? (
              <button
                onClick={() => { setTarget(null); setPickerOpen(true); }}
                className="text-sm font-semibold text-text-primary hover:text-brand truncate text-left"
                title="Change recipient"
                data-testid="quick-dm-change-recipient"
              >
                To: {target.name || target.user_id}
              </button>
            ) : (
              <p className="text-sm font-semibold text-text-primary">Choose recipient</p>
            )}
          </div>
          <button
            onClick={close}
            disabled={sending}
            className="text-text-muted hover:text-text-primary disabled:opacity-30"
            data-testid="quick-dm-close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        {!target && pickerOpen ? (
          <div className="flex flex-col flex-1 min-h-0">
            <div className="p-3 border-b border-border-subtle">
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-text-muted" />
                <input
                  type="search"
                  autoFocus
                  placeholder="Search teammates"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-border-subtle rounded-md focus:outline-none focus:border-brand bg-surface-page"
                  data-testid="quick-dm-picker-search"
                />
              </div>
            </div>
            <div className="overflow-y-auto flex-1">
              {membersLoading ? (
                <div className="p-6 text-center"><Loader2 size={18} className="animate-spin text-brand mx-auto" /></div>
              ) : filteredMembers.length === 0 ? (
                <p className="p-6 text-sm text-text-muted text-center">No matches</p>
              ) : (
                filteredMembers.map((m) => (
                  <button
                    key={m.email || m.slack?.user_id}
                    onClick={() => {
                      setTarget({
                        user_id: m.slack.user_id,
                        team_id: m.slack.team_id,
                        name: m.full_name,
                        image: m.image,
                      });
                      setPickerOpen(false);
                      setTimeout(() => textareaRef.current?.focus(), 50);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 hover:bg-surface-page text-left transition-colors"
                    data-testid={`quick-dm-pick-${m.email}`}
                  >
                    <Avatar name={m.full_name} image={m.image} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-text-primary truncate">{m.full_name}</p>
                      {m.title && <p className="text-[11px] text-text-tertiary truncate">{m.title}</p>}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : (
          <>
            <div className="p-4 space-y-2">
              <textarea
                ref={textareaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Type your message…  *bold*  _italic_  Cmd+Enter to send"
                rows={6}
                className={`w-full px-3 py-2 text-sm border rounded-md resize-none focus:outline-none ${
                  overLimit ? 'border-red-400 focus:border-red-500' : 'border-border-subtle focus:border-brand'
                }`}
                data-testid="quick-dm-textarea"
                disabled={sending}
              />
              <div className="flex items-center justify-between text-[11px]">
                <span className="text-text-tertiary">Sent as Ventas OS bot · prefixed with your name</span>
                <span className={overLimit ? 'text-red-600 font-medium' : 'text-text-muted'}>{remaining}</span>
              </div>
            </div>

            <div className="px-4 py-3 bg-surface-page flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={close} disabled={sending} data-testid="quick-dm-cancel">
                Cancel
              </Button>
              <Button
                onClick={send}
                disabled={!text.trim() || overLimit || sending}
                className="bg-chart-purple hover:bg-chart-purple/90 text-white"
                data-testid="quick-dm-send"
              >
                {sending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />}
                {sending ? 'Sending…' : 'Send'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default QuickDmModal;
